import React, { FormEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  OutlinedInput,
  Pagination,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteModal } from "components/DeleteModal";
import { toast } from "react-toastify";
import {
  BlackListPayload,
  BlackListRegistro,
  useDeleteBlackListMutation,
  useGetBlackListQuery,
  usePostBlackListMutation,
  useUpdateBlackListMutation,
} from "shared/features/api/blackList/blackListSlice";

const initialForm = {
  nome: "",
  email: "",
};

const initialFormErrors = {
  nome: "",
  email: "",
};

const DATA_GRID_ROW_HEIGHT = 52;
const DATA_GRID_HEADER_HEIGHT = 56;
const DATA_GRID_VERTICAL_OFFSET = 260;

const getBlackListTableHeight = () => {
  if (typeof window === "undefined") {
    return 520;
  }

  return Math.max(260, Math.floor(window.innerHeight * 0.95 - DATA_GRID_VERTICAL_OFFSET));
};

const getBlackListPageSize = () => {
  const tableHeight = getBlackListTableHeight();
  const availableRowsHeight = tableHeight - DATA_GRID_HEADER_HEIGHT;

  return Math.max(1, Math.floor(availableRowsHeight / DATA_GRID_ROW_HEIGHT));
};

export const BlackList: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(getBlackListPageSize);
  const [tableHeight, setTableHeight] = useState(getBlackListTableHeight);
  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRegistro, setSelectedRegistro] =
    useState<BlackListRegistro | null>(null);
  const [form, setForm] = useState(initialForm);
  const [formErrors, setFormErrors] = useState(initialFormErrors);

  const { data, isLoading, isFetching } = useGetBlackListQuery({
    pagina: page,
    tamanho: pageSize,
    filtro,
  });
  const [postBlackList, { isLoading: isCreating }] = usePostBlackListMutation();
  const [updateBlackList, { isLoading: isUpdating }] =
    useUpdateBlackListMutation();
  const [deleteBlackList, { isLoading: isDeleting }] =
    useDeleteBlackListMutation();

  const isSaving = isCreating || isUpdating;

  useEffect(() => {
    if (data?.quantidadePaginas && page >= data.quantidadePaginas) {
      setPage(Math.max(data.quantidadePaginas - 1, 0));
    }
  }, [data?.quantidadePaginas, page]);

  useEffect(() => {
    const handleResize = () => {
      const nextTableHeight = getBlackListTableHeight();
      const nextPageSize = getBlackListPageSize();

      setTableHeight(nextTableHeight);
      setPageSize((currentPageSize) =>
        currentPageSize === nextPageSize ? currentPageSize : nextPageSize
      );
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(0);
      setFiltro(search.trim());
    }, 500);

    return () => clearTimeout(timeout);
  }, [search]);

  const getErrorMessage = (error: unknown, fallback: string) => {
    const response = error as {
      data?: {
        errors?: unknown[];
        message?: string;
      };
    };

    const firstError = response.data?.errors?.[0];

    if (typeof firstError === "string") {
      return firstError;
    }

    if (firstError && typeof firstError === "object") {
      const errorObject = firstError as {
        message?: string;
        mensagem?: string;
        defaultMessage?: string;
      };

      return (
        errorObject.message ||
        errorObject.mensagem ||
        errorObject.defaultMessage ||
        fallback
      );
    }

    return response.data?.message || fallback;
  };

  const validateForm = () => {
    const nome = form.nome.trim();
    const email = form.email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const errors = { ...initialFormErrors };

    if (nome.length < 3) {
      errors.nome = "O nome deve ter no mínimo 3 caracteres.";
    } else if (nome.length > 100) {
      errors.nome = "O nome deve ter no máximo 100 caracteres.";
    }

    if (email.length > 60) {
      errors.email = "O e-mail deve ter no máximo 60 caracteres.";
    } else if (email.length > 0 && !emailRegex.test(email)) {
      errors.email = "Informe um e-mail válido.";
    }

    setFormErrors(errors);

    return !errors.nome && !errors.email;
  };

  const handleOpenCreate = () => {
    setSelectedRegistro(null);
    setForm(initialForm);
    setFormErrors(initialFormErrors);
    setOpenForm(true);
  };

  const handleOpenEdit = (registro: BlackListRegistro) => {
    setSelectedRegistro(registro);
    setForm({
      nome: registro.nome,
      email: registro.email,
    });
    setFormErrors(initialFormErrors);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedRegistro(null);
    setForm(initialForm);
    setFormErrors(initialFormErrors);
  };

  const handleOpenDelete = (registro: BlackListRegistro) => {
    setSelectedRegistro(registro);
    setOpenDelete(true);
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
    setFiltro(search.trim());
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload: BlackListPayload = {
      nome: form.nome.trim(),
    };

    const email = form.email.trim();

    if (email) {
      payload.email = email;
    }

    if (!validateForm()) {
      toast.warning("Verifique os campos do formulário antes de salvar.");
      return;
    }

    const toastId = toast.loading("Salvando...");

    try {
      if (selectedRegistro) {
        await updateBlackList({
          idBlackList: selectedRegistro.idBlackList,
          body: payload,
        }).unwrap();
      } else {
        await postBlackList(payload).unwrap();
      }

      toast.update(toastId, {
        render: selectedRegistro
          ? "Registro atualizado com sucesso!"
          : "Registro cadastrado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });

      handleCloseForm();
    } catch (error) {
      toast.update(toastId, {
        render: getErrorMessage(error, selectedRegistro ? "Erro ao atualizar registro." : "Erro ao cadastrar registro."),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedRegistro) return;

    const toastId = toast.loading("Removendo...");

    try {
      await deleteBlackList(selectedRegistro.idBlackList).unwrap();

      toast.update(toastId, {
        render: "Registro removido com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });

      setSelectedRegistro(null);
    } catch (error) {
      toast.update(toastId, {
        render: getErrorMessage(error, "Erro ao remover registro."),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const columns = [
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "email",
      headerName: "E-mail",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "acoes",
      headerName: "Ações",
      flex: 0.5,
      minWidth: 110,
      align: "center" as const,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const registro = params.row.registro as BlackListRegistro;

        return (
          <Box display="flex" justifyContent="center" gap={1} width="100%">
            <Tooltip title="Editar">
              <span>
                <IconButton
                  onClick={() => handleOpenEdit(registro)}
                  disabled={isSaving || isDeleting}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Excluir">
              <span>
                <IconButton
                  onClick={() => handleOpenDelete(registro)}
                  disabled={isSaving || isDeleting}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const rows =
    data?.elementos.map((registro) => ({
      id: registro.idBlackList,
      nome: registro.nome,
      email: registro.email,
      registro,
    })) ?? [];

  return (
    <Grid
      container
      rowSpacing={2}
      columnSpacing={0}
      sx={{
        width: "100%",
        maxWidth: "100%",
        m: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <Grid item xs={12}>
        <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="search-black-list">
              Pesquisar por nome ou email
            </InputLabel>
            <OutlinedInput
              id="search-black-list"
              label="Pesquisar por nome ou email"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end" type="submit">
                    <Search color="primary" />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </form>
      </Grid>

      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ mb: { xs: 1, sm: 0 } }}>
          Total de registros: <strong>{data?.totalElementos ?? 0}</strong>
        </Box>
        <Button
          sx={{
            height: "3rem",
            width: { xs: "100%", sm: "auto" },
          }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          disabled={isSaving || isDeleting}
        >
          Cadastrar
        </Button>
      </Grid>

      <Grid
        item
        xs={12}
        sx={{
          height: tableHeight,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "hidden",
        }}
      >
        {isLoading || isFetching ? (
          <LinearProgress />
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={[pageSize]}
            rowHeight={DATA_GRID_ROW_HEIGHT}
            headerHeight={DATA_GRID_HEADER_HEIGHT}
            hideFooter
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxShadow: 2,
              "& .MuiDataGrid-cell": {
                fontSize: "14px",
              },
              "& .MuiDataGrid-main": {
                overflowX: "hidden",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "hidden",
              },
              "& .MuiDataGrid-columnHeaders": {
                fontSize: "16px",
                whiteSpace: "nowrap",
              },
              "& .MuiDataGrid-row": {
                maxHeight: "40px",
              },
            }}
          />
        )}
      </Grid>

      <Grid item xs={12} display="flex" justifyContent="center">
        <Pagination
          count={data?.quantidadePaginas ?? 1}
          color="primary"
          size="small"
          onChange={(_, newPage) => setPage(newPage - 1)}
          page={page + 1}
        />
      </Grid>

      <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedRegistro ? "Editar registro" : "Cadastrar registro"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField
                label="Nome"
                value={form.nome}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    nome: event.target.value,
                  }))
                }
                fullWidth
                required
                error={!!formErrors.nome}
                helperText={formErrors.nome}
                inputProps={{ maxLength: 100, minLength: 3 }}
                disabled={isSaving}
              />
              <TextField
                label="E-mail"
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                fullWidth
                error={!!formErrors.email}
                helperText={formErrors.email}
                inputProps={{ maxLength: 60 }}
                disabled={isSaving}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={isSaving}>
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <DeleteModal
        open={openDelete}
        setOpen={setOpenDelete}
        title="Confirmar remoção"
        context="Realmente deseja remover este registro da Black List?"
        deleteFunction={handleDelete}
      />
    </Grid>
  );
};
